import * as React from "react";
import * as Sentry from "@sentry/react";
import { type FileRejection, useDropzone } from "react-dropzone";
import { Check } from "lucide-react";
import axios from "axios";
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody } from "@/components/ui/table";
import { useToastDefault } from "@/hooks/use-toast-default";
import * as analytics from "@/services/analytics";
import { FileWithProgress, UploadResponse } from "./types";
import { ACCEPTED_EXTENSIONS, BATCHED_UPLOAD_SIZE, FRONTEND_UPLOAD_FACTOR, MAX_UPLOAD_SIZE } from "./consts";
import { formatFileErrors, processNewFiles } from "./helpers";
import { UploadExistsFilesConfirmationModal } from "../uploadExistsFilesConfirmationModal";
import { UploadModalPermissionNotification } from "./permissionNotifications";
import { UploadModalWebsocket } from "./websocket";
import { UploadModalFilesHeader } from "./filesHeader";
import { UploadModalFilesRow } from "./filesRow";
import { UploadModalErrors } from "@/views/drive/modals/uploadModal/errors";
import { checkFileExists, FILES_TAG, FILES_TAG_SEARCH, filesApi } from "@/api/files";
import { useAppDispatch } from "@/store/hooks";

interface Props {
  currentFolderId?: string;
  setIsOpen: (isOpen: boolean) => void;
}

export const UploadModalContent: React.FC<Props> = ({ setIsOpen, currentFolderId }) => {
  const [files, setFiles] = React.useState<FileWithProgress[]>([]);
  const [isUploading, setIsUploading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [uploadComplete, setUploadComplete] = React.useState(false);
  const [existFiles, setExistFiles] = React.useState<File[]>([]);
  const { showToast } = useToastDefault();

  const dispatch = useAppDispatch();

  React.useEffect(() => {
    if (uploadComplete) {
      setIsUploading(false);
      dispatch(filesApi.util.invalidateTags([FILES_TAG_SEARCH, FILES_TAG]));
    }
  }, [files, uploadComplete, dispatch]);

  const handleUpload = React.useCallback(
    async (validFiles: FileWithProgress[]) => {
      if (validFiles.length === 0) return;

      setIsUploading(true);
      setUploadComplete(false);

      const formData = new FormData();
      validFiles.forEach(fileWithProgress => {
        formData.append("files", fileWithProgress.file);
      });

      try {
        const response = await axios.put<UploadResponse[]>("/pf-document/upload", formData, {
          params: { fse_id: currentFolderId || "" },
          onUploadProgress: progressEvent => {
            const progress = progressEvent?.progress || 0;
            setFiles(prevFiles =>
              prevFiles.map(file => ({
                ...file,
                progress: progress * FRONTEND_UPLOAD_FACTOR * 100,
              })),
            );
          },
        });

        validFiles.forEach(file => {
          analytics.documentUpload(file.file.name, file.file.size);
        });
        setIsUploading(false);
        dispatch(filesApi.util.invalidateTags([FILES_TAG_SEARCH, FILES_TAG]));

        setFiles(prevFiles =>
          prevFiles.map(file => {
            const uploadedFile = response.data.find(f => f.name === file.file.name);

            return uploadedFile
              ? {
                  ...file,
                  fse_id: uploadedFile.fse_id,
                  document_id: uploadedFile.document_id,
                  status: "started" as const,
                }
              : file;
          }),
        );
      } catch (error) {
        Sentry.captureException(error);
        setIsUploading(false);
        setFiles(prevFiles =>
          prevFiles.map(file => ({
            ...file,
            status: "error",
            error: "Upload failed",
          })),
        );
      }
    },
    [currentFolderId, dispatch],
  );

  const handleUploadBatch = React.useCallback(
    async (newFiles: FileWithProgress[]) => {
      const filteredFiles = newFiles.filter(file => !file.error && file.status === "pending");
      for (let i = 0; i < filteredFiles.length; i += BATCHED_UPLOAD_SIZE) {
        const batched = filteredFiles.slice(i, i + BATCHED_UPLOAD_SIZE);
        void handleUpload(batched);
      }
    },
    [handleUpload],
  );

  const onDropAcceptedFilesProcess = React.useCallback(
    async (acceptedFiles: readonly File[]) => {
      const readyToUpload: File[] = [];
      const duplicatedFiles: File[] = [];

      await Promise.all(
        acceptedFiles.map(async file => {
          const result = await checkFileExists(file.name);

          if (!result) {
            readyToUpload.push(file);
          } else {
            duplicatedFiles.push(file);
          }
        }),
      );

      setExistFiles(duplicatedFiles);

      const newFiles = processNewFiles(readyToUpload);

      setFiles(prevFiles => [...prevFiles, ...newFiles]);

      void handleUploadBatch(newFiles);
    },
    [handleUploadBatch],
  );

  const onDrop = React.useCallback(
    (acceptedFiles: readonly File[], fileRejections: readonly FileRejection[]) => {
      const rejectedFiles = fileRejections.map(({ file, errors }) => ({
        file,
        progress: 0,
        status: "error" as const,
        error: formatFileErrors(errors),
        tx_id: "",
      }));
      setFiles(prevFiles => [...prevFiles, ...rejectedFiles]);
      void onDropAcceptedFilesProcess(acceptedFiles);
    },
    [onDropAcceptedFilesProcess],
  );

  React.useEffect(() => {
    if (files.length > 0 && files.every(file => file.status === "completed")) {
      setUploadComplete(true);
    }
  }, [files]);

  const removeFile = React.useCallback((fileToRemove: FileWithProgress) => {
    setFiles(prevFiles => prevFiles.filter(file => file !== fileToRemove));
  }, []);

  const resetUploadState = React.useCallback(() => {
    setFiles([]);
    setIsUploading(false);
    setUploadComplete(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleClose = React.useCallback(() => {
    setIsOpen(false);
    resetUploadState();
  }, [setIsOpen, resetUploadState]);

  React.useEffect(() => {
    if (uploadComplete) {
      // We wanna show the toast after the upload is completed, and modal is closed
      setTimeout(() => {
        showToast("Upload completed!", "Success! Your files have now been uploaded");
        handleClose();
      }, 500);
    }
  }, [uploadComplete, showToast, handleClose]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: ACCEPTED_EXTENSIONS,
    maxSize: MAX_UPLOAD_SIZE,
  });

  const errorMessage = files
    .filter(file => !!file.error)
    .map(file => `${file.file.name} ${file.error}`)
    .join(", ");

  return (
    <DialogContent className="flex h-[80vh] max-h-[600px] w-full max-w-[90vw] flex-col">
      {!!existFiles.length && (
        <UploadExistsFilesConfirmationModal
          setFiles={setFiles}
          handleUpload={handleUploadBatch}
          setExistFiles={setExistFiles}
          files={existFiles}
        />
      )}
      {currentFolderId && <UploadModalPermissionNotification handleClose={handleClose} fseId={currentFolderId} />}
      <DialogHeader>
        <DialogTitle>Upload Files</DialogTitle>
      </DialogHeader>
      <div className="flex flex-grow flex-col space-y-4 overflow-hidden">
        <UploadModalWebsocket setFiles={setFiles} />
        {files.length === 0 ? (
          <div
            {...getRootProps()}
            className="flex flex-grow cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed p-6 text-center hover:border-primary hover:bg-primary/10"
          >
            <input {...getInputProps()} ref={fileInputRef} />
            <p className="text-lg font-semibold">Drag &apos;n&apos; drop some files here</p>
            <p className="text-sm text-gray-500">or</p>
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="mt-2">
              Choose File
            </Button>
            <em className="mt-4 text-sm text-gray-500">(Only .pdf and .docx will be accepted)</em>
          </div>
        ) : (
          <div className="flex-grow overflow-auto">
            <Table>
              <UploadModalFilesHeader />
              <TableBody>
                {files.map((fileWithProgress, index) => (
                  <UploadModalFilesRow
                    key={index}
                    isUploading={isUploading}
                    fileWithProgress={fileWithProgress}
                    onFileRemove={removeFile}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      {errorMessage && <UploadModalErrors errorMessage={errorMessage} />}
      <div className="mt-4 text-right">
        <Button variant="outline" onClick={handleClose}>
          <Check className="mr-2 h-5 w-5" />
          Close
        </Button>
      </div>
    </DialogContent>
  );
};
